import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { 
  getProjectAccessProviders, 
  ProjectAccessProvider,
  canLeaveReview 
} from '../lib/api/reviews';
import ReviewModal from './ReviewModal';

interface ProjectCompletionModalProps {
  open: boolean;
  onClose: () => void;
  onProjectCompleted: () => void;
  projectId: string;
  projectTitle: string;
}

const ProjectCompletionModal: React.FC<ProjectCompletionModalProps> = ({
  open,
  onClose,
  onProjectCompleted,
  projectId,
  projectTitle,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<ProjectAccessProvider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<ProjectAccessProvider | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [canReview, setCanReview] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (open) {
      loadProviders();
    }
  }, [open, projectId]);

  const loadProviders = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await getProjectAccessProviders(projectId);

      if (error) {
        setError(error);
        return;
      }

      if (data) {
        setProviders(data);
        
        // Check which providers can be reviewed
        const reviewChecks = await Promise.all(
          data.map(async (provider) => {
            const { canReview: canReviewProvider } = await canLeaveReview(projectId, provider.provider_id);
            return { providerId: provider.provider_id, canReview: canReviewProvider };
          })
        );

        const canReviewMap = reviewChecks.reduce((acc, check) => {
          acc[check.providerId] = check.canReview;
          return acc;
        }, {} as { [key: string]: boolean });

        setCanReview(canReviewMap);
      }
    } catch (err) {
      setError('Неочаквана грешка при зареждане на изпълнителите');
      console.error('Error loading providers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderSelect = (provider: ProjectAccessProvider) => {
    if (canReview[provider.provider_id]) {
      setSelectedProvider(provider);
      setReviewModalOpen(true);
    }
  };

  const handleReviewSubmitted = () => {
    setReviewModalOpen(false);
    setSelectedProvider(null);
    onProjectCompleted();
  };

  const handleClose = () => {
    setProviders([]);
    setSelectedProvider(null);
    setCanReview({});
    setError(null);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('bg-BG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            color: '#ffffff',
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            🎉 Проектът е завършен!
          </Typography>
          <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
            {projectTitle}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 3, color: '#ffffff' }}>
                Кой изпълнител направи проекта реалност?
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 3, color: '#a0a0a0' }}>
                Изберете изпълнител, който е закупил достъп до проекта и е работил по него, 
                за да му оставите ревю.
              </Typography>

              {providers.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Няма изпълнители, които са закупили достъп до този проект.
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {providers.map((provider) => (
                    <Grid item xs={12} sm={6} key={provider.provider_id}>
                      <Card
                        sx={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          cursor: canReview[provider.provider_id] ? 'pointer' : 'not-allowed',
                          opacity: canReview[provider.provider_id] ? 1 : 0.6,
                          transition: 'all 0.3s ease',
                          '&:hover': canReview[provider.provider_id] ? {
                            background: 'rgba(40, 160, 240, 0.1)',
                            borderColor: '#28A0F0',
                            transform: 'translateY(-2px)',
                          } : {},
                        }}
                        onClick={() => handleProviderSelect(provider)}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar
                              src={provider.provider_avatar}
                              sx={{ width: 50, height: 50, bgcolor: '#28A0F0' }}
                            >
                              <PersonIcon />
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                                {provider.provider_name}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                                Закупил достъп на {formatDate(provider.access_purchased_at)}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Chip 
                              label={t('common.purchasedAccess')} 
                              color="success" 
                              size="small"
                              sx={{ 
                                background: 'rgba(76, 175, 80, 0.2)',
                                color: '#4caf50',
                                border: '1px solid rgba(76, 175, 80, 0.3)'
                              }}
                            />
                            {canReview[provider.provider_id] && (
                              <Chip 
                                label={t('common.canBeReviewed')} 
                                color="primary" 
                                size="small"
                                icon={<StarIcon />}
                                sx={{ 
                                  background: 'rgba(40, 160, 240, 0.2)',
                                  color: '#28A0F0',
                                  border: '1px solid rgba(40, 160, 240, 0.3)'
                                }}
                              />
                            )}
                          </Box>

                          {canReview[provider.provider_id] ? (
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<CheckIcon />}
                              sx={{
                                background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                                color: '#ffffff',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                                },
                              }}
                            >
                              Оставете ревю
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              fullWidth
                              disabled
                              sx={{
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                color: 'rgba(255, 255, 255, 0.5)',
                              }}
                            >
                              Вече ревюиран
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              <Typography variant="body2" sx={{ color: '#a0a0a0', textAlign: 'center' }}>
                💡 Можете да оставите ревю само на изпълнители, които са закупили достъп до проекта.
                Това гарантира, че ревютата са от реални кандидати.
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: '#a0a0a0',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Затвори
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Modal */}
      {selectedProvider && (
        <ReviewModal
          open={reviewModalOpen}
          onClose={() => setReviewModalOpen(false)}
          onReviewSubmitted={handleReviewSubmitted}
          projectId={projectId}
          projectTitle={projectTitle}
          provider={{
            id: selectedProvider.provider_id,
            name: selectedProvider.provider_name,
            avatar: selectedProvider.provider_avatar,
          }}
        />
      )}
    </>
  );
};

export default ProjectCompletionModal;
